import { NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { BrowserModule } from '@angular/platform-browser';
import { LocationStrategy, PathLocationStrategy } from '@angular/common';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppRoutingModule } from './app-routing.module';

import { BadgeModule } from 'primeng/badge';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { CheckboxModule } from 'primeng/checkbox';
import { DividerModule } from 'primeng/divider';
import { DropdownModule } from 'primeng/dropdown';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputMaskModule } from 'primeng/inputmask';
import { InputTextModule } from 'primeng/inputtext';
import { MenuModule } from 'primeng/menu';
import { MessagesModule } from 'primeng/messages';
import { MessageModule } from 'primeng/message';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { TabMenuModule } from 'primeng/tabmenu';
import { TableModule } from 'primeng/table';
import { TimelineModule } from 'primeng/timeline';

import { AppComponent } from './app.component';
import { AppMainComponent } from './menu/app.main.component';
import { AppMenuComponent } from './menu/app.menu.component';
import { AppMenuitemComponent } from './menu/app.menuitem.component';
import { AppRightPanelComponent } from './menu/app.rightpanel.component';
import { AppBreadcrumbComponent } from './menu/app.breadcrumb.component';
import { AppTopBarComponent } from './menu/app.topbar.component';
import { AppBreadcrumbService } from './menu/app.breadcrumb.service';
import { MenuService } from './menu/app.menu.service';
import { AppMenuButtonComponent } from './menu/app.menubutton.component';

// Store
import { MetaReducer, StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import * as fromApp from './store/app.reducer';
import * as fromClearState from './store/clear-state.reducer';
import { AuthEffects } from './store/auth/auth.effects';

import { environment } from 'src/environments/environment';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { AuthInterceptor } from './interceptors/auth.interceptor';
import { PageNotFoundComponent } from './components/page-not-found/page-not-found.component';
import { UserEffects } from './store/user/user.effects';
import { TicketEffects } from './store/ticket/ticket.effects';
import { SiteEffects } from './store/site/site.effects';
import { DeviceListEffects } from './store/device-list/device-list.effects';
import { EventEffects } from './store/event/event.effects';
import { DeviceEffects } from './store/device/device.effects';
import { TaskEffects } from './store/task/task.effects';
import { SiteMapEffects } from './store/site-map/site-map.effects';
import { SiteListEffects } from './store/site-list/site-list.effects';
import { UserFileEffects } from './store/user-file/user-file.effects';
import { SceneryEffects } from './store/scenery/scenery.effects';
import { ScheduleEffects } from './store/schedule/schedule.effects';
import { CalendarEffects } from './store/calendar/calendar.effects';
import { HelpEffects } from './store/help/help.effects';
import { PermissionEffects } from './store/permissions/permissions.effects';
import { ServiceEffects } from './store/service/service.effects';
import { BusCalculationModule } from './modules/bus-calculation/bus-calculation.module';
import { AccessCompModule } from './modules/access/access.module';
import { HomeModule } from './components/home/home.module';
import { NotificationsModule } from './modules/shared/components/notifications/notifications.module';
import { SharedModule } from './modules/shared/shared.module';
import { AppDownloadModule } from './modules/appdownload/appdownload.module';
import { PasswordUpdateModule } from './modules/password-update/password-update.module';
import { GreenPower3Module } from './modules/greenpower3/green-power3.module';
import { GreenPowerModule } from './modules/greenpower/green-power.module';
import { GreenPower2Module } from './modules/greenpower2/green-power2.module';
import { ListDeviceSupportModule } from './modules/list-device-support/list-device-support.module';
import { AnalyticsEffects } from './store/analytics/analytics.effects';
import { EmailUnsubscribeModule } from './modules/email-unsubscribe/email-unsubscribe.module';
import { DeviceVctEffects } from './store/device-vct/device-vct.effects';
import { DeviceThEffects } from './store/device-th/device-th.effects';
import { InvitationsEffects } from './store/invitations/invitations.effects';
import { DeviceMtmaEffects } from './store/device-mtma/device-mtma.effects';

export const HttpLoaderFactory = (http: HttpClient): TranslateHttpLoader => new TranslateHttpLoader(http, './assets/i18n/');

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const metaReducers: MetaReducer<any>[] = [fromClearState.clearState];

@NgModule({
  imports: [
    BrowserModule,
    FormsModule,
    AppRoutingModule,
    HttpClientModule,
    BrowserAnimationsModule,
    BadgeModule,
    BreadcrumbModule,
    ButtonModule,
    CalendarModule,
    CheckboxModule,
    DividerModule,
    DropdownModule,
    InputNumberModule,
    InputMaskModule,
    InputTextModule,
    MenuModule,
    MessageModule,
    MessagesModule,
    OverlayPanelModule,
    TableModule,
    TabMenuModule,
    TimelineModule,
    BusCalculationModule,
    GreenPower3Module,
    GreenPowerModule,
    GreenPower2Module,
    ListDeviceSupportModule,
    AppDownloadModule,
    AccessCompModule,
    SharedModule,
    HomeModule,
    PasswordUpdateModule,
    EmailUnsubscribeModule,
    StoreModule.forRoot(fromApp.reducers, {
      metaReducers, runtimeChecks: {
        strictStateImmutability: false,
        strictActionImmutability: false,
      },
    }),
    EffectsModule.forRoot([
      AuthEffects, UserEffects, TicketEffects, SiteEffects, DeviceListEffects, EventEffects, DeviceEffects, TaskEffects,
      SiteMapEffects, SiteListEffects, UserFileEffects, SceneryEffects, ScheduleEffects, CalendarEffects, PermissionEffects,
      HelpEffects, ServiceEffects, AnalyticsEffects, DeviceVctEffects, DeviceThEffects, DeviceMtmaEffects,
      HelpEffects, ServiceEffects, AnalyticsEffects, InvitationsEffects,
    ]),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      logOnly: environment.production
    }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      },
    }),
    NotificationsModule,
  ],
  declarations: [
    AppComponent,
    AppMainComponent,
    AppMenuComponent,
    AppMenuitemComponent,
    AppRightPanelComponent,
    AppBreadcrumbComponent,
    AppTopBarComponent,
    PageNotFoundComponent,
    AppMenuButtonComponent
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: LocationStrategy, useClass: PathLocationStrategy },
    MenuService,
    AppBreadcrumbService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule { }
